import { useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { Colors, Theme } from '@app/theme';
import { getMaxNumberOfQuestions } from 'lib/billing';
import { Offer, OfferType, ShortOfferDto } from '@app/api';
import { useTranslation } from 'react-i18next';
import { usePricingColor } from './ColorContext';
import classNames from 'classnames';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    fontSize: theme.iconSize.regular,
    marginRight: theme.marginBase,
    color: ({ color }: { color: Colors }) => theme.colorDefinitions[color].std,
  },
  text: {
    ...theme.fonts.label,
  },
  all: {
    color: ({ color }: { color: Colors }) =>
      theme.colorDefinitions[color].accessible,
  },
}));

interface Props {
  offer: Partial<Offer> & ShortOfferDto;
}

export const OfferScreenerIncluded = ({ offer }: Props) => {
  const color = usePricingColor();
  const { t } = useTranslation();
  const classes = useStyles({ color });

  const questionCount = useMemo(() => {
    if (
      offer.type !== OfferType.TARGETING ||
      !('products' in offer) ||
      !offer.products
    ) {
      return -1;
    }

    const products = offer.products;
    return getMaxNumberOfQuestions(products);
  }, [offer]);

  return questionCount === -1 ? (
    <div className={classes.container}>
      <i className={classNames('ri-check-line', classes.icon)} />
      <div className={classNames(classes.text, classes.all)}>
        {t('billing.offers.block.items.screeningQuestionsCount_all')}
      </div>
    </div>
  ) : questionCount === 0 ? (
    <div className={classes.container}>
      <i className={classNames('ri-close-line', classes.icon)} />
      <div className={classes.text}>
        {t('billing.offers.block.items.screeningQuestionsCount', {
          count: questionCount,
        })}
      </div>
    </div>
  ) : (
    <div className={classes.container}>
      <i className={classNames('ri-lock-line', classes.icon)} />
      <div className={classes.text}>
        {t('billing.offers.block.items.screeningQuestionsCount', {
          count: questionCount,
        })}
      </div>
    </div>
  );
};
